import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import classes from "./StudioErstellen.module.css";
import {
    normalizeAvailabilityForWizard,
    useStudioErstellen
} from '../../store/studioErstellenStore';
import { v4 as uuid } from 'uuid';
import { useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { useCreateStudio } from "../../hooks/useCreateStudio";
import { refetchUserStudios } from '../../util/refetchUserStudios';
import { publishStudioCreationWithOwnedMedia } from './studioCreationMediaFlow';
import LoadingCreationModal from "./LoadingCreationModal.jsx";
import { notifySaveError } from '../../util/notifyActionError';

import CancelPopUp from "./CancelPopUp.jsx";
import WelcomingWindow from "./WelcomingWindow.jsx";

import Standort from "./Standort.jsx";
import TypeOfStudio from "./TypeOfStudio.jsx";
// import Beschreibung from "./Beschreibung.jsx"
import Amenities from "./Amenities.jsx";
import StudioTitle from "./StudioTitle.jsx";
import StudioDescription from "./StudioDescription.jsx";
import FotosInput from "./FotosInput.jsx";
import StudioSize from "./StudioSize.jsx";
import Preis from "./Preis.jsx";
import Availability from "./Availability.jsx";
import LastStep from "./LastStep.jsx";
import StudioEquipment from "./StudioEquipment/StudioEquipment.jsx";
import EquipmentOptional from "./EquipmentOptional.jsx";
import Stornierungsbedingungen from "./Stornierungsbedingungen.jsx";
import Rules from "./Rules.jsx";
import { NavBarContext } from "../../App";
import { createAddressValidationKey } from "../../util/addressValidation";
import {
    trackStudioCreationComplete,
    trackStudioCreationStep,
} from "../../util/tracking";

const stepRoutes = [
    'willkommen',
    // 'equipment-optional-test',
    'standort',         
    'studio-typ',        
    // 'beschreibung',
    'titel',             
    'beschreibung-detail', 
    'ausstattung',        
    'equipment',          
    'equipment-optional',
    'fotos',              
    'stornierung',        
    'hausregeln',         
    'groesse',   
    'preis',              
    'betriebszeiten',         
    'veroeffentlichen'    
];

const isUsableStudioImage = (img) => img?.file instanceof File && img.file.size > 0;

const toBackendCoordinates = (coordinates) => {
    if (!coordinates) return null;
    const latitude = coordinates.latitude ?? coordinates.lat;
    const longitude = coordinates.longitude ?? coordinates.lng;

    if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
        return null;
    }

    return {
        latitude: Number(latitude),
        longitude: Number(longitude)
    };
};

export const StudioErstellen = () => {
    const { studioData, clearStudioData, updateStudioData, markStepCompleted } = useStudioErstellen();

    const userID = useSelector((state) => state.auth.user._id);
    const apolloClient = useApolloClient();
    const { handleStudioCreation, loading, error } = useCreateStudio();
    const { step } = useParams();
    const navigate = useNavigate();
    const { isPhoneSize } = useContext(NavBarContext);
    const location = useLocation();

    const [hasError, setHasError] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const isPublishingRef = useRef(false);
    const isDirectWizardEntryRef = useRef(false);
    const hasInternalHashRef = useRef(typeof window !== 'undefined' ? (window.location?.hash || '').includes('from=internal') : false);
    const cameFromInternalStateAtEntryRef = useRef(false);
    const lastTrackedStudioCreationStepRef = useRef(null);

    const [hasActiveSession, setHasActiveSession] = useState(false);
    const sessionIdRef = useRef(null);
    const WIZARD_SESSION_KEY = 'studioErstellenActiveSession';
    const SESSION_STORAGE_KEY = 'studioErstellenSessionId';

    // Exit confirmation modal state
    const [showExitModal, setShowExitModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const isNavigatingRef = useRef(false);

    const cleanupWizardState = () => {
        clearStudioData();
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
        localStorage?.removeItem(WIZARD_SESSION_KEY);
    };

    const getOrCreateSessionId = () => {
        try {
            let existingSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (!existingSession) {
                existingSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                sessionStorage.setItem(SESSION_STORAGE_KEY, existingSession);
            }
            sessionIdRef.current = existingSession;
            return existingSession;
        } catch (error) {
            // Fallback if sessionStorage is unavailable
            const fallbackSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionIdRef.current = fallbackSession;
            return fallbackSession;
        }
    };

    useEffect(() => {
        // Reuse existing session ID if present (survives reloads)
        const currentSessionId = getOrCreateSessionId();
        
        const checkActiveSession = () => {
            const activeSession = localStorage.getItem(WIZARD_SESSION_KEY);
            const sessionData = activeSession ? JSON.parse(activeSession) : null;

            if (sessionData && sessionData.sessionId && sessionData.sessionId !== sessionIdRef.current) {
                // Another tab has an active session
                const timeDiff = Date.now() - sessionData.timestamp;
                
                // If session is less than 10 seconds old, another tab is active
                if (timeDiff < 10000) {
                    setHasActiveSession(true);
                    return false;
                }
            }
            return true;
        };
        
        const claimSession = () => {
            if (checkActiveSession()) {
                const sessionDataToPersist = {
                    sessionId: currentSessionId,
                    timestamp: Date.now()
                };
                localStorage.setItem(WIZARD_SESSION_KEY, JSON.stringify(sessionDataToPersist));
                setHasActiveSession(false);
            }
        };
    
    claimSession();
    
    // Heartbeat to keep session alive
    const heartbeatInterval = setInterval(() => {
        if (!hasActiveSession) {
                const sessionDataToPersist = {
                    sessionId: sessionIdRef.current,
                timestamp: Date.now()
            };
                localStorage.setItem(WIZARD_SESSION_KEY, JSON.stringify(sessionDataToPersist));
        }
    }, 3000); // Update every 3 seconds
    
    // Listen for localStorage changes from other tabs
    const handleStorageChange = (e) => {
        if (e.key === WIZARD_SESSION_KEY && e.newValue) {
            const newSession = JSON.parse(e.newValue);
            if (newSession.sessionId !== sessionIdRef.current) {
                // Another tab claimed the session
                setHasActiveSession(true);
            }
        }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup on unmount or page close
        const cleanup = () => {
            const activeSession = localStorage.getItem(WIZARD_SESSION_KEY);
            if (activeSession) {
                const sessionData = JSON.parse(activeSession);
                if (sessionData.sessionId === sessionIdRef.current) {
                    localStorage.removeItem(WIZARD_SESSION_KEY);
                }
            }
    };
    
    window.addEventListener('beforeunload', cleanup);
    
        return () => {
            clearInterval(heartbeatInterval);
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('beforeunload', cleanup);
            cleanup();
        };
    }, [hasActiveSession]);

    // Check if user has entered any data worth saving
    const hasUnsavedData = () => {
        return !!(
            studioData?.StudioName ||
            studioData?.standort?.city ||
            studioData?.standort?.street ||
            studioData?.studioTypes?.length > 0 ||
            studioData?.selectedTags?.length > 0 ||
            studioData?.description ||
            studioData?.selectedAmenities?.length > 0 ||
            studioData?.images?.length > 0 ||
            studioData?.studioSize ||
            studioData?.capacity ||
            studioData?.pricePerHour ||
            studioData?.equipment ||
            (Array.isArray(studioData?.paidEquipment) && studioData.paidEquipment.length > 0) ||
            (Array.isArray(studioData?.optionalBookableEquipment) && studioData.optionalBookableEquipment.length > 0)
        );
    };

    // Warn before closing/refreshing browser tab
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedData() && !isPublishingRef.current) {
                e.preventDefault();
                e.returnValue = ''; // Required for Chrome
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [studioData]);

    // Handle link click interception
    const handleExitClick = (e, targetPath) => {
        if (hasUnsavedData() && !isNavigatingRef.current) {
            e.preventDefault();
            setPendingNavigation(() => () => navigate(targetPath));
            setShowExitModal(true);
        }
    };

    // Confirm exit and proceed with navigation
    const confirmExit = () => {
        isNavigatingRef.current = true;
        updateStudioData('images', []);
        cleanupWizardState();
        setShowExitModal(false);
        
        if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
        }
    };

    // Cancel exit and stay in wizard
    const cancelExit = () => {
        setShowExitModal(false);
        setPendingNavigation(null);
    };

    const welcomingWindowValidation = useRef(() => true);
    // const equipmentOptionalTestValidation = useRef(() => true);
    const standortValidation = useRef(() => true);
    const typeOfStudioValidation = useRef(() => true);
    // const beschreibungValidation = useRef(() => true);
    const studioTitleValidation = useRef(() => true);
    const studioDescriptionValidation = useRef(() => true);
    const fotosInputValidation = useRef(() => true);
    const studioSizeValidation = useRef(() => true);
    const studioEquipmentValidation = useRef(() => true);
    const amenitiesValidation = useRef(() => true);
    const stornierungsbedingungenValidation = useRef(() => true);
    const rulesValidation = useRef(() => true);
    const preisValidation = useRef(() => true);
    const availabilityValidation = useRef(() => true);
    const equipmentOptionalValidation = useRef(() => true);
    const lastStepValidation = useRef(() => true);       
    
    const getCurrentSlideFromURL = () => {
        if (!step) return 0;
        const index = stepRoutes?.indexOf(step);
        return index >= 0 ? index : 0;
    };

    const [currentSlide, setCurrentSlide] = useState(getCurrentSlideFromURL());

    const slides = [
        <WelcomingWindow validate={welcomingWindowValidation}/>,
        // <EquipmentOptional fromEditor={false} />,
        <Standort validate={standortValidation}/>, 
        <TypeOfStudio validate={typeOfStudioValidation}/>, 
        // <Beschreibung validate={beschreibungValidation}/>s, 
        <StudioTitle validate={studioTitleValidation}/>, 
        <StudioDescription validate={studioDescriptionValidation}/>, 
        <Amenities validate={amenitiesValidation}/>,
        <StudioEquipment validate={studioEquipmentValidation}/>,
        <EquipmentOptional validate={equipmentOptionalValidation} fromEditor={false} />,
        <FotosInput validate={fotosInputValidation}/>, 
        <Stornierungsbedingungen validate={stornierungsbedingungenValidation}/>,
        <Rules validate={rulesValidation}/>,    
        <StudioSize validate={studioSizeValidation}/>,
        <Preis validate={preisValidation}/>,
        <Availability validate={availabilityValidation}/>,
        <LastStep validate={lastStepValidation}/>
    ];
    
    const totalSlides = slides?.length;

    // Data validation functions that check if step data is complete in the store
    const validateStepData = (stepIndex) => {
        const completedSteps = studioData?.completedSteps || [];
        
        switch(stepIndex) {
            case 0: // WelcomingWindow
                return true; // No validation needed for welcoming step
            
            // case 1:
            //     return true;
            
            case 1: // Standort
                return studioData?.standort?.city?.trim() && 
                       studioData?.standort?.street?.trim() && 
                       studioData?.standort?.postalCode?.toString()?.trim() &&
                       studioData?.standort?.country?.trim();
            
            case 2: // TypeOfStudio
                return studioData?.studioTypes && studioData?.studioTypes?.length > 0;
            
            // case 4: // Beschreibung (tags)
            //     return studioData?.selectedTags && studioData?.selectedTags?.length > 0;
            
            case 3: // StudioTitle
                return studioData?.StudioName?.trim()?.length > 0;
            
            case 4: // StudioDescription
                return studioData?.description?.trim()?.length > 0;
            
            case 5: // Amenities (optional, but step must be visited)
                // markStepCompleted uses the slide index when leaving this step
                return completedSteps.includes(5);
            
            case 6: // StudioEquipment (optional, but step must be visited)
                return completedSteps.includes(6);
            
            case 7: // PaidEquipment (optional, but step must be visited)
                return completedSteps.includes(7);

            case 8: { // FotosInput
                const hasValidImages = studioData?.images &&
                    studioData?.images?.length >= 5 &&
                    studioData?.images?.every(isUsableStudioImage);
                return hasValidImages;
            }

            case 9: // Stornierungsbedingungen (optional, but step must be visited)
                return completedSteps.includes(9);

            case 10: // Rules (optional, but step must be visited)
                return completedSteps.includes(10);

            case 11: // StudioSize
                return studioData?.studioSize && studioData?.capacity;

            case 12: { // Preis
                const pricingType = studioData?.pricingType;

                if (pricingType === "stundenpreis") {
                    // Check minNumberOfHours first
                    if (!studioData?.minNumberOfHours) {
                        return false;
                    }
                    
                    // If adjustPriceByPeople is true, check priceGroups instead of pricePerHour
                    if (studioData?.adjustPriceByPeople) {
                        return Array.isArray(studioData?.priceGroups) && 
                               studioData.priceGroups.length > 0 &&
                               studioData.priceGroups.some(group => group.price && parseFloat(group.price) >= 10);
                    } else {
                        // Simple hourly pricing - check pricePerHour
                        return studioData?.pricePerHour && parseFloat(studioData.pricePerHour) >= 10;
                    }
                }

                if (pricingType === "paketpreise") {
                    // Für Paketpreise: mindestens ein Paket erforderlich
                    return Array.isArray(studioData?.packages) && studioData.packages.length > 0;
                }

                // Falls noch kein Pricing-Typ gewählt wurde, Schritt gilt als nicht valide
                return false;
            }
            
            case 13: // Availability
                return studioData?.availability && Object.keys(studioData?.availability).length > 0;
            
            case 14: // LastStep
                return true; // Last step doesn't need validation to access
            
            default:
                return false;
        }
    };

    const canAccessStep = (stepIndex) => {
        if (stepIndex === 0) return true;
        
        // Check if all previous steps have valid data
        for (let i = 0; i < stepIndex; i++) {
            const isValid = validateStepData(i);
            if (!isValid) {
                return false;
            }
        }
        
        return true;
    };

    const getFirstIncompleteStep = () => {
        for (let i = 0; i < totalSlides; i++) {
            const isValid = validateStepData(i);
            if (!isValid) {
                return i;
            }
        }
        return 0;
    };

    const validateCurrentSlide = async () => {
        const validateFunctions = [
            welcomingWindowValidation.current,
            // equipmentOptionalTestValidation.current,
            standortValidation.current,
            typeOfStudioValidation.current,
            // beschreibungValidation.current,
            studioTitleValidation.current,
            studioDescriptionValidation.current,
            amenitiesValidation.current,
            studioEquipmentValidation.current,
            equipmentOptionalValidation.current,
            fotosInputValidation.current,
            stornierungsbedingungenValidation.current,
            rulesValidation.current,
            studioSizeValidation.current,
            preisValidation.current,
            availabilityValidation.current,
        ];

        const isValid = await validateFunctions[currentSlide]();
        
        setHasError(!isValid);
        return isValid;
    };

    // Sync currentSlide with URL changes (for browser back/forward)
    useEffect(() => {
        const newSlide = getCurrentSlideFromURL();
        const canAccess = canAccessStep(newSlide);

        if (!canAccess) {
            // Redirect to the first incomplete step
            const firstIncomplete = getFirstIncompleteStep();

            if (firstIncomplete === 8) {
                updateStudioData('images', []);
            }

            const hashValue = hasInternalHashRef.current ? 'from=internal' : undefined;
            const to = hashValue 
                ? { pathname: `/studio-erstellen/${stepRoutes[firstIncomplete]}`, hash: hashValue } 
                : { pathname: `/studio-erstellen/${stepRoutes[firstIncomplete]}` };
            navigate(to, { replace: true });
            return;
        }

        if (newSlide !== currentSlide) {
            setCurrentSlide(newSlide);
        }
    }, [step, currentSlide, navigate, studioData?.completedSteps, studioData?.pricingType, studioData?.adjustPriceByPeople, studioData?.pricePerHour, studioData?.priceGroups, studioData?.minNumberOfHours]);

    // Navigate to first step if no step is specified, preserving internal hash if present
    useEffect(() => {
        if (!step) {
            const hashValue = (window.location?.hash || '').replace('#', '') || (hasInternalHashRef.current ? 'from=internal' : '');
            const to = hashValue ? { pathname: `/studio-erstellen/${stepRoutes[0]}`, hash: hashValue } : { pathname: `/studio-erstellen/${stepRoutes[0]}` };
            navigate(to, { replace: true });
        }
    }, [step, navigate, stepRoutes]);

    const progressPercentage = ((currentSlide + 1) / totalSlides) * 100;

    const carouselRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const carouselEl = carouselRef?.current;
        if (!carouselEl) return;

        if (isPhoneSize && containerRef?.current) {
            const containerEl = containerRef.current;
            const targetTopWithinContainer = carouselEl.offsetTop - (containerEl.offsetTop || 0);
            containerEl.scrollTo({ top: targetTopWithinContainer - 100, left: 0, behavior: 'auto' });
        } else {
            const rect = carouselEl.getBoundingClientRect();
            const absoluteTop = rect.top + (window.pageYOffset || document.documentElement.scrollTop || 0);
            window.scrollTo({ top: absoluteTop, left: 0, behavior: 'auto' });
        }
    }, [currentSlide, isPhoneSize]);

    // Handle browser back button with popstate event  
    useEffect(() => {
        let lastPath = window.location.pathname;
        
        const handlePopState = (event) => {
            const currentPath = window.location.pathname;
            const wasInWizard = lastPath.includes('/studio-erstellen');
            const isStillInWizard = currentPath.includes('/studio-erstellen');
            
            // Check if we're navigating away from the wizard
            const isLeavingWizard = wasInWizard && !isStillInWizard;
            
            // If leaving wizard and have unsaved data, show confirmation
            if (isLeavingWizard && hasUnsavedData() && !isNavigatingRef.current && !isPublishingRef.current) {
                // Prevent the navigation by going forward again
                const currentStepRoute = step || stepRoutes[currentSlide] || stepRoutes[0];
                
                // Push back to wizard
                window.history.pushState(
                    { wizardIntercepted: true }, 
                    '', 
                    `/studio-erstellen/${currentStepRoute}`
                );
                
                // Show the exit modal
                setPendingNavigation(() => () => {
                    isNavigatingRef.current = true;
                    window.history.back();
                });
                setShowExitModal(true);
                
                // Don't update lastPath since we prevented navigation
                return;
            }
            
            // Update last path for next popstate
            lastPath = currentPath;
        };
        
        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [currentSlide, step, studioData, isPublishing]);

    // Detect if user entered directly on a deep wizard URL (typed URL/bookmark) and capture internal hash flag/state
    useEffect(() => {
        try {
            const referrer = document?.referrer || '';
            const currentSlideIndex = getCurrentSlideFromURL();
            let sameOrigin = false;
            try {
                sameOrigin = referrer ? new URL(referrer).origin === window.location.origin : false;
            } catch (_) {
                sameOrigin = false;
            }
            const cameFromWizard = sameOrigin && referrer.includes('/studio-erstellen/');
            const hasInternalHash = (window.location?.hash || '').includes('from=internal');
            const cameFromInternalState = location?.state?.from === 'internal';
            hasInternalHashRef.current = hasInternalHash;
            cameFromInternalStateAtEntryRef.current = !!cameFromInternalState;
            // Treat any deep entry not coming from wizard referrer as direct wizard entry
            isDirectWizardEntryRef.current = currentSlideIndex > 0 && !cameFromWizard;
        } catch (_) {
            const hasInternalHash = (window.location?.hash || '').includes('from=internal');
            const cameFromInternalState = location?.state?.from === 'internal';
            hasInternalHashRef.current = hasInternalHash;
            cameFromInternalStateAtEntryRef.current = !!cameFromInternalState;
            isDirectWizardEntryRef.current = getCurrentSlideFromURL() > 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canGoBackWithinApp = () => {
        try {
            const hasBack = window.history.length > 1;
            const ref = document?.referrer || '';
            let sameOrigin = false;
            try {
                sameOrigin = ref ? new URL(ref).origin === window.location.origin : false;
            } catch (_) {
                sameOrigin = false;
            }
            const fromInternalState = cameFromInternalStateAtEntryRef.current;
            // Heuristic only: cannot know the exact next URL due to browser security
            // Prefer trusted app signal (link state) over referrer when available
            if (fromInternalState && hasBack) return true;
            return hasBack && sameOrigin;
        } catch (error) {
            return false;
        }
    };


    const nextSlide = async () => {
        const isValid = await validateCurrentSlide();

        if (isValid) {
            // Mark current step as completed before moving to next
            markStepCompleted(currentSlide);
            
            if (currentSlide < totalSlides - 1) {
                const nextStep = stepRoutes[currentSlide + 1];
                const hashValue = hasInternalHashRef.current ? 'from=internal' : undefined;
                const to = hashValue ? { pathname: `/studio-erstellen/${nextStep}`, hash: hashValue } : { pathname: `/studio-erstellen/${nextStep}` };
                navigate(to);
                // Once navigating within wizard, treat subsequent back as history back
                isDirectWizardEntryRef.current = false;
            }
        }
    };

    const prevSlide = () => {        
        if (currentSlide === 0) {
            // Show confirmation modal if user has unsaved data
            if (hasUnsavedData()) {
                if (canGoBackWithinApp()) {
                    setPendingNavigation(() => () => navigate(-1));
                } else {
                    setPendingNavigation(() => () => navigate('/'));
                }
                setShowExitModal(true);
                return;
            }
            // No unsaved data, navigate normally
            if (canGoBackWithinApp()) {
                navigate(-1);
            } else {
                navigate('/');
            }
            return;
        }
        if (isDirectWizardEntryRef.current) {
            const prevStep = stepRoutes[currentSlide - 1];
            const hashValue = hasInternalHashRef.current ? 'from=internal' : undefined;
            const to = hashValue ? { pathname: `/studio-erstellen/${prevStep}`, hash: hashValue } : { pathname: `/studio-erstellen/${prevStep}` };
            navigate(to);
        } else {
            navigate(-1);
        }
    };

    const stepTexts = [
        "Studio erstellen",
        // "Equipment optional (Test)",
        "Schritt 1: Standort",
        // "Schritt 2: Art des Studios",
        "Schritt 2: Art des Studios",
        "Schritt 3: Titel & Beschreibung",
        "Schritt 3: Titel & Beschreibung",
        "Schritt 4: Grundausstattung",
        "Schritt 5: Studio Equipment",
        "Schritt 6: Kostenpflichtiges Equipment",
        "Schritt 7: Fotos hochladen",
        "Schritt 8: Stornierungsbedingungen",
        "Schritt 9: Hausregeln",
        "Schritt 10: Größe & Kapazität",
        "Schritt 11: Preis & Stundenanzahl",
        "Schritt 12: Betriebszeiten",
        "Schritt 13: Veröffentlichen"
    ];
    const currentStepText = stepTexts[currentSlide];

    useEffect(() => {
        const signature = `${currentSlide}:${currentStepText}`;
        if (lastTrackedStudioCreationStepRef.current === signature) return;

        lastTrackedStudioCreationStepRef.current = signature;
        trackStudioCreationStep({
            stepIndex: currentSlide + 1,
            stepName: currentStepText,
        });
    }, [currentSlide, currentStepText]);

    const handleExit = () => {
        isNavigatingRef.current = true;
        updateStudioData('images', []);
        cleanupWizardState();
        navigate('/');
    };

    const handlePublish = async () => {
        if (isPublishing) return;

        try {
            setIsPublishing(true);
            isPublishingRef.current = true;

            const isPackagesMode = studioData?.pricingType === 'paketpreise';
            const isHourlyMode = studioData?.pricingType === 'stundenpreis';
            const hasTieredPricing = isHourlyMode && studioData?.adjustPriceByPeople && studioData?.priceGroups?.length > 0;
            
            // For hourly pricing: check pricePerHour OR priceGroups (if tiered pricing)
            const hasValidPricing = isPackagesMode ? true : (
                hasTieredPricing 
                    ? studioData?.priceGroups?.some(group => group.price && parseFloat(group.price) >= 10)
                    : studioData?.pricePerHour && parseFloat(studioData.pricePerHour) >= 10
            );
            
            const requiredFields = {
                StudioName: studioData?.StudioName?.trim(),
                standort: studioData?.standort?.city && studioData?.standort?.street && studioData?.standort?.postalCode,
                pricePerHour: hasValidPricing, // Valid pricing (either pricePerHour or priceGroups)
                description: studioData?.description?.trim(),
                studioSize: studioData?.studioSize,
                numberOfPeople: studioData?.capacity,
                minNumberOfHours: isPackagesMode ? true : studioData?.minNumberOfHours, // Not required for PACKAGES
                studioTypes: studioData?.studioTypes,
                tagValues: studioData?.tagValues || [],
                pricingType: studioData?.pricingType, // Must be selected
                images: Array.isArray(studioData?.images) &&
                    studioData.images.length >= 5 &&
                    studioData.images.every(isUsableStudioImage)
            };

            const missingFields = Object.entries(requiredFields)
                .filter(([_, value]) => !value)
                .map(([key]) => key);

            if (missingFields.length > 0) {
                throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
            }

            const studioId = uuid();
            const usableImages = studioData.images.filter(isUsableStudioImage);

            const paidEquipmentSource = Array.isArray(studioData?.paidEquipment)
                ? studioData.paidEquipment
                : Array.isArray(studioData?.optionalBookableEquipment)
                  ? studioData.optionalBookableEquipment
                  : [];

            // Map cancellation policy values to enum
            const cancellationPolicyMap = {
                'kulant': 'FLEXIBLE',
                'standard': 'STANDARD',
                'strikt': 'STRICT'
            };
            const mappedCancellationPolicy = studioData?.cancellationPolicy 
                ? cancellationPolicyMap[studioData.cancellationPolicy] || null 
                : null;

            // Transform house rules from array to HouseRules object
            const transformHouseRules = (rules) => {
                if (!rules || !Array.isArray(rules) || rules.length === 0) {
                    return null;
                }

                const houseRules = {};
                const ruleMapping = {
                    'smoking': 'smokingAllowed',
                    'alcohol': 'alcoholAllowed',
                    'food': 'foodAllowed',
                    'fire': 'pyrotechnicsAllowed',
                    'party': 'glitterConfettiAllowed',
                    'adult': 'adultContentAllowed',
                    'fogMachine': 'fogMachineAllowed'
                };

                // Find equipmentChangesAllowed - not in current options, default to false
                houseRules.equipmentChangesAllowed = false;

                rules.forEach(rule => {
                    if (rule.value && ruleMapping[rule.value] && rule.allowed !== null) {
                        houseRules[ruleMapping[rule.value]] = rule.allowed === true;
                    }
                });

                // Get custom rules from extraRules
                if (studioData?.extraRules && studioData.extraRules.length > 0) {
                    houseRules.customRules = studioData.extraRules[0].title || '';
                } else {
                    houseRules.customRules = null;
                }

                // Ensure all required fields are present (set to false if not specified)
                const requiredFields = [
                    'smokingAllowed', 'alcoholAllowed', 'foodAllowed', 
                    'equipmentChangesAllowed', 'pyrotechnicsAllowed', 
                    'glitterConfettiAllowed', 'adultContentAllowed', 'fogMachineAllowed'
                ];
                requiredFields.forEach(field => {
                    if (houseRules[field] === undefined) {
                        houseRules[field] = false;
                    }
                });

                return houseRules;
            };

            const houseRules = transformHouseRules(studioData?.rules);

            // Helper functions for safe parsing (defined before transformPricingData)
            const safeParseInt = (value, defaultValue = 0) => {
                const parsed = parseInt(value);
                return isNaN(parsed) ? defaultValue : parsed;
            };

            const safeParseFloat = (value, defaultValue = 0) => {
                const parsed = parseFloat(value);
                return isNaN(parsed) ? defaultValue : parsed;
            };

            // Transform pricing data for backend
            const transformPricingData = () => {
                const pricingType = studioData?.pricingType;
                const isHourly = pricingType === 'stundenpreis';
                const isPackages = pricingType === 'paketpreise';
                
                const pricingData = {
                    pricingMode: isPackages ? 'PACKAGES' : 'HOURLY'
                };
                
                if (isHourly) {
                    // HOURLY mode
                    if (studioData?.adjustPriceByPeople && studioData?.priceGroups?.length > 0) {
                        // Tiered pricing
                        pricingData.pricePerHour = null;
                        pricingData.tieredPricing = studioData.priceGroups.map(group => ({
                            minPersons: safeParseInt(group.from, 1),
                            maxPersons: safeParseInt(group.to, safeParseInt(studioData?.capacity, 20)),
                            pricePerHour: safeParseFloat(group.price, 0)
                        }));
                    } else if (studioData?.pricePerHour) {
                        // Simple hourly pricing (no tiered pricing)
                        pricingData.pricePerHour = safeParseFloat(studioData.pricePerHour, 0);
                    }
                    
                    // minNumberOfHours only for HOURLY mode
                    if (studioData?.minNumberOfHours) {
                        pricingData.minNumberOfHours = safeParseInt(studioData.minNumberOfHours, 0);
                    }
                } else if (isPackages) {
                    // PACKAGES mode
                    pricingData.pricePerHour = null;
                    pricingData.minNumberOfHours = null;
                    pricingData.tieredPricing = null;
                    if (studioData?.packages?.length > 0) {
                        pricingData.packages = studioData.packages.map(pkg => {
                            const hasGroups = studioData?.adjustPriceByPeople && studioData?.priceGroups?.length > 0;
                            const groups = hasGroups
                                ? studioData.priceGroups
                                : [{
                                    id: 'all',
                                    from: 1,
                                    to: safeParseInt(studioData?.capacity, 20)
                                }];
                            
                            // Convert prices object to array format
                            const pricesArray = groups.map(group => {
                                const groupId = hasGroups ? group.id : 'all';
                                const price = (pkg.prices && pkg.prices[groupId]) || "0";
                                const parsedPrice = safeParseFloat(price, 0);
                                // Ensure price is a valid number (not NaN or null)
                                if (isNaN(parsedPrice) || parsedPrice === null || parsedPrice === undefined) {
                                    console.warn('[StudioErstellen] Invalid price found for package:', pkg.id, 'group:', groupId, 'price:', price);
                                }
                                return {
                                    minPersons: safeParseInt(group.from, 1),
                                    maxPersons: safeParseInt(group.to, safeParseInt(studioData?.capacity, 20)),
                                    price: isNaN(parsedPrice) ? 0 : parsedPrice
                                };
                            });
                            
                            return {
                                id: pkg.id || `pkg-${Date.now()}-${Math.random()}`,
                                name: pkg.name || `${pkg.hours} ${safeParseInt(pkg.hours, 0) === 1 ? 'Stunde' : 'Stunden'}`,
                                durationHours: safeParseInt(pkg.hours, 0),
                                prices: pricesArray
                            };
                        });
                    }
                    // Note: minNumberOfHours is NOT required for PACKAGES mode
                }
                
                // Discount rules (both modes)
                if (studioData?.discounts?.length > 0) {
                    pricingData.discountRules = studioData.discounts.map(discount => {
                        const discountValue = safeParseFloat(discount.discountValue, 0);
                        return {
                            minHours: safeParseInt(discount.minHours, 0),
                            discountType: discount.discountType === 'percentage' ? 'PERCENTAGE' : 'FIXED',
                            discountValue: isNaN(discountValue) ? 0 : discountValue
                        };
                    });
                }
                
                // Weekend surcharge (both modes)
                if (studioData?.weekendSurcharge && studioData?.weekendDays?.length > 0) {
                    const saturday = studioData.weekendDays.find(d => d.day === 'Samstag');
                    const sunday = studioData.weekendDays.find(d => d.day === 'Sonntag');
                    
                    pricingData.weekendSurcharge = {
                        saturdayEnabled: !!saturday,
                        saturdaySurchargeType: saturday ? (studioData.weekendMode === 'percentage' ? 'PERCENTAGE' : 'FIXED') : null,
                        saturdaySurchargeValue: saturday ? safeParseFloat(saturday.percent, 0) : null,
                        sundayEnabled: !!sunday,
                        sundaySurchargeType: sunday ? (studioData.weekendMode === 'percentage' ? 'PERCENTAGE' : 'FIXED') : null,
                        sundaySurchargeValue: sunday ? safeParseFloat(sunday.percent, 0) : null
                    };
                }
                
                // Extra fees (both modes)
                if (studioData?.fees?.length > 0) {
                    pricingData.extraFees = studioData.fees.map(fee => {
                        const amount = safeParseFloat(fee.amount, 0);
                        return {
                            id: fee.id || `fee-${Date.now()}-${Math.random()}`,
                            name: fee.description || '',
                            amount: isNaN(amount) ? 0 : amount
                        };
                    });
                }
                
                return pricingData;
            };
            
            const pricingData = transformPricingData();
            const isHourly = studioData?.pricingType === 'stundenpreis';
            const currentAddressKey = createAddressValidationKey({
                street: studioData?.standort?.street,
                postalCode: studioData?.standort?.postalCode,
                city: studioData?.standort?.city,
                country: studioData?.standort?.country
            });
            const validatedCoordinates =
                studioData?.validatedAddress?.addressKey === currentAddressKey
                    ? toBackendCoordinates(studioData.validatedAddress.coordinates)
                    : null;
            const publishCoordinates = validatedCoordinates;

            const result = await publishStudioCreationWithOwnedMedia({
                studioId,
                images: usableImages,
                paidEquipmentSource,
                handleStudioCreation,
                buildStudioData: ({ studioId: ownedMediaStudioId, imageKeys, paidEquipmentItems }) => {
                    const validImageKeys = imageKeys.filter(key => key);

                    if (validImageKeys.length === 0) {
                        throw new Error("No images could be uploaded");
                    }

                    return {
                        id: ownedMediaStudioId,
                        StudioName: studioData?.StudioName,
                        studioLocation: studioData?.standort?.city,
                        images: validImageKeys,
                        description: studioData?.description,
                        userID: userID,
                        studioTypes: studioData?.studioTypes,
                        tagValues: studioData?.tagValues || [],
                        studioSize: safeParseInt(studioData?.studioSize),
                        // productionAreaSize is optional – only send when filled
                        ...(studioData?.productionAreaSize && {
                            productionAreaSize: safeParseInt(studioData?.productionAreaSize)
                        }),
                        numberOfPeople: safeParseInt(studioData?.capacity),
                        amenities: studioData?.selectedAmenities || [],
                        streetAndNumber: studioData?.standort?.street,
                        adressLine2: studioData?.standort?.additionalAddress || null,
                        city: studioData?.standort?.city,
                        postcode: String(studioData?.standort?.postalCode || ''),
                        country: studioData?.standort?.country,
                        ...(publishCoordinates && {
                            latitude: publishCoordinates.latitude,
                            longitude: publishCoordinates.longitude
                        }),
                        minNumberOfHours: isHourly ? safeParseInt(studioData?.minNumberOfHours) : undefined,
                        equipment: studioData?.equipment || {},
                        paidEquipmentItems,
                        availability: normalizeAvailabilityForWizard(studioData?.availability),
                        tags: studioData?.selectedTags,
                        ...pricingData,
                        ...(mappedCancellationPolicy && { cancellationPolicy: mappedCancellationPolicy }),
                        ...(houseRules && { houseRules })
                    };
                },
            });
            
            if (result) {
                trackStudioCreationComplete({
                    studioId: result?.id,
                    pricingMode: pricingData?.pricingMode,
                });

                try {
                    await refetchUserStudios(apolloClient);
                } catch (refetchError) {
                    console.warn('[StudioErstellen] Studio was created, but refetch failed:', refetchError);
                }

                // Clear all studio data from store (this also clears localStorage)
                clearStudioData();
                
                // Clear session storage
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.removeItem(SESSION_STORAGE_KEY);
                }
                
                // Clear wizard session from localStorage
                localStorage?.removeItem(WIZARD_SESSION_KEY);
                
                const newUrl = `/preview/studio/${result?.id}`;

                navigate(newUrl, {
                    replace: true,
                    state: {
                        from: 'studio-creation',
                        showSuccessModal: true
                    }
                });

            }

        } catch (error) {
            if (error.cleanupError) {
                console.warn('Studio-Erstellen-Media-Cleanup nach fehlgeschlagenem Publish ist fehlgeschlagen:', error.cleanupError);
            }
            console.error('Studio konnte nicht veröffentlicht werden:', error);
            notifySaveError(error, {
                title: 'Studio konnte nicht veröffentlicht werden',
                message: 'Studio konnte nicht veröffentlicht werden. Bitte versuche es erneut.',
            });
            
            // Reset the ref on error so user can try again or exit
            isPublishingRef.current = false;
            setIsPublishing(false);
            // You might want to add some error handling UI here
        }
        // Note: We don't reset isPublishingRef in finally because if successful,
        // we're navigating away and don't want beforeunload to trigger
    };

    useEffect(() => {
        return () => {
            if (studioData.images) {
                studioData.images.forEach(image => {
                    if (image.url) URL.revokeObjectURL(image.url);
                });
            }
        };
    }, [studioData.images]);

    useEffect(() => {
        return () => {
            // If the wizard unmounts without publish success, ensure stale form data is not kept.
            if (!isPublishingRef.current) {
                cleanupWizardState();
            }
        };
    }, []);

  return (
    <div 
        ref={containerRef}
        style={{backgroundColor: currentSlide === 8 ? '#FAFAFA' : 'white'}}  
        className={classes.container}
    >
        {hasActiveSession && (
            <div style={{
                position: 'fixed',
                height: '100vh',
                width: '100vw',
                top: 0,
                left: 0,
                backgroundColor: 'rgba(255, 255, 255)',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '20px'
            }}>
                <h2 className="titleSemibold" style={{ fontSize: '24px', color: '#1F1F1F' }}>
                    Studio-Erstellung bereits geöffnet
                </h2>
                <p className="regularLarge" style={{ maxWidth: '500px', textAlign: 'center', color: '#666' }}>
                    Du arbeitest bereits in einem anderen Tab oder Fenster an der Studio-Erstellung. 
                    Bitte schließe dieses Fenster oder beende die Bearbeitung im anderen Tab.
                </p>
            </div>
        )}
        <header className={classes.header}>
            {/* <div className={classes.headerContainer}>
            <Link to="/" className={classes.logoContainer} onClick={(e) => handleExitClick(e, '/')}>
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                    <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                    <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                    <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
                <span className={`${classes.currentStep} regularMiddle`}>{stepTexts[currentSlide]}</span>
            </Link>
            <Link to="/" className={classes.closeContainer} onClick={(e) => handleExitClick(e, '/')}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>  
            </Link>
            <button className={`${classes.exitButton} regularMiddle`} onClick={handleExit}>Speichern & beenden</button>
            </div> */}
            <div className={classes.headerContainer2} style={{ justifyContent: 'flex-start', gap: '100px' }}>
                <Link to="/" className={classes.logoContainer} onClick={(e) => handleExitClick(e, '/')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                        <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                        <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                        <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                    </svg>
                </Link>
                <Link to="/" className={classes.closeContainer} onClick={(e) => handleExitClick(e, '/')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                    </svg>  
                </Link>
                <div className={classes.headerButtonsContainer}>
                    <span className={`${classes.currentStep} regularMiddle`}>{stepTexts[currentSlide]}</span>
                    {/* <button className={`${classes.exitButton} regularMiddle`} onClick={handleExit}>Speichern & beenden</button> */}
                </div>
            </div>
            <div className={classes.progressBarTop}>
                <div
                    className={classes.progressBarInner}
                    style={{ width: `${progressPercentage}%` }}
                ></div>
            </div>
        </header>
        <div 
            className={`${classes.carousel} ${currentSlide === slides?.length - 2 ? classes.availabilityWidth : ''}`}
            ref={carouselRef}
            style={{backgroundColor: currentSlide === 8 ? '#FAFAFA' : 'white'}}
        >
            <div className={`${classes.slide}`}>
                {slides[currentSlide]}
            </div>
        </div>
        <footer className={classes.footer}>
            <div className={classes.progressBar}>
                <div
                    className={classes.progressBarInner}
                    style={{ width: `${progressPercentage}%` }}
                ></div>
            </div>
            <div className={classes.footerContainer}>

                    {!hasActiveSession && (
                        <div onClick={prevSlide} className={classes.footerButtonsContainer}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                                <line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7"/>
                                <line x1="0.626536" y1="8.54651" x2="9.62508" y2="18.2949" stroke="#1F1F1F" strokeWidth="1.7"/>
                            </svg>
                                <button className={`${classes.footerBackButton} mediumLarge`}>Zurück</button>
                        </div>
                    )}

                {currentSlide === 0 && !hasActiveSession && (
                    <button 
                        onClick={nextSlide} 
                        className={`${classes.footerWeiterButton} mediumLarge`}
                        style={{ background: 'var(--stu-red-dark)', color: 'white' }}
                    >
                        Loslegen
                    </button>
                )}
                {currentSlide !== 0 && currentSlide !== slides.length - 1 && !hasActiveSession && (
                    <button 
                        onClick={nextSlide} 
                        className={`${classes.footerWeiterButton} mediumLarge`}
                    >
                        Weiter
                    </button>
                )}
                {currentSlide === slides.length - 1 && !hasActiveSession && (
                    <button 
                        onClick={handlePublish} 
                        disabled={isPublishing || loading}
                        className={`${classes.footerWeiterButton} mediumLarge`}
                    >
                        {isPublishing || loading ? 'Veröffentlichung...' : 'Veröffentlichen'}
                    </button>
                )}
            </div>
        </footer>

        {/* Exit Confirmation Modal */}
        <CancelPopUp 
            showExitModal={showExitModal} 
            cancelExit={cancelExit} 
            confirmExit={confirmExit} 
        />
        
        {/* Loading Creation Modal */}
        {isPublishing && <LoadingCreationModal studioImage={studioData?.images?.[0]?.previewUrl}/> }
    </div>
  );
};

export default StudioErstellen;
